import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';
import { Popup } from '../common/Popup';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabaseClient';
import { buildOAuthCallbackUrl, buildRegisterRedirectPath } from '../routing/loginRedirect';
import { SocialAuthButtons } from './SocialAuthButtons';
import {
    clearAuthUpgradeNavigationPending,
    markAuthUpgradeNavigationPending
} from '../../utils/authUpgradeNavigation';

function getCurrentAppPath() {
    if (typeof window === 'undefined') return '/';
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function isIdentityAlreadyLinkedError(error) {
    return error?.code === 'identity_already_exists' ||
        (
            typeof error?.message === 'string' &&
            error.message.toLowerCase().includes('already linked')
        );
}

function isEmailAlreadyRegisteredError(error) {
    const code = String(error?.code || '').toLowerCase();
    const message = String(error?.message || '').toLowerCase();
    return (
        ['email_exists', 'user_already_exists', 'email_already_registered'].includes(code) ||
        message.includes('already registered') ||
        message.includes('already exists')
    );
}

function isWeakPasswordError(error) {
    const code = String(error?.code || '').toLowerCase();
    const message = String(error?.message || '').toLowerCase();
    return (
        code === 'weak_password' ||
        message.includes('weak password') ||
        (message.includes('password') && message.includes('characters'))
    );
}

function SignupGateContent({
    description,
    disabled,
    email,
    emailHref,
    error,
    isEmailFormOpen,
    isGuest,
    message,
    onEmailChange,
    onEmailSubmit,
    onOpenEmailForm,
    onPasswordChange,
    onPasswordConfirmChange,
    onProvider,
    password,
    passwordConfirm,
    title
}) {
    const action = isGuest ? 'Save your team with' : 'Continue with';

    return (
        <div className="signup-gate-content">
            <p className="signup-gate-kicker">Keep your progress</p>
            <h3>{title}</h3>
            <p>{description}</p>
            <p className="signup-gate-note">
                Your current squad and match progress stay with you.
            </p>
            {error ? <div className="error-message" role="alert">{error}</div> : null}
            <div className="form-block">
                <SocialAuthButtons
                    disabled={disabled}
                    googleLabel={`${action} Google`}
                    xLabel={`${action} X`}
                    onGoogle={() => onProvider('google', 'Google')}
                    onX={() => onProvider('x', 'X')}
                />
            </div>
            {emailHref ? (
                <Link to={emailHref} className="signup-gate-email-link">
                    Continue with email
                </Link>
            ) : null}
            {isGuest ? (
                <>
                    <button
                        type="button"
                        className="signup-gate-email-link signup-gate-email-button"
                        onClick={onOpenEmailForm}
                        disabled={disabled}
                        aria-expanded={isEmailFormOpen}
                    >
                        Continue with email
                    </button>
                    {isEmailFormOpen ? (
                        <form className="signup-gate-email-form" onSubmit={onEmailSubmit}>
                            <label htmlFor="signup-gate-email">
                                Email
                                <input
                                    id="signup-gate-email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={onEmailChange}
                                    disabled={disabled}
                                    required
                                />
                            </label>
                            <label htmlFor="signup-gate-password">
                                Password
                                <input
                                    id="signup-gate-password"
                                    type="password"
                                    autoComplete="new-password"
                                    minLength={8}
                                    value={password}
                                    onChange={onPasswordChange}
                                    disabled={disabled}
                                    required
                                />
                            </label>
                            <label htmlFor="signup-gate-password-confirm">
                                Confirm password
                                <input
                                    id="signup-gate-password-confirm"
                                    type="password"
                                    autoComplete="new-password"
                                    minLength={8}
                                    value={passwordConfirm}
                                    onChange={onPasswordConfirmChange}
                                    disabled={disabled}
                                    required
                                />
                            </label>
                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={disabled}
                            >
                                {disabled ? 'Saving...' : 'Save my squad'}
                            </button>
                        </form>
                    ) : null}
                </>
            ) : null}
            {message ? <div className="signup-gate-message" role="status">{message}</div> : null}
        </div>
    );
}

function SignupGateModal({
    description = 'Create your free account to unlock the transfer market.',
    inline = false,
    onDismiss,
    open = true,
    redirectTo,
    title = 'Sign up to improve your team'
}) {
    const {
        session,
        user,
        isGuest: authIsGuest,
        loginWithOAuth,
        ensureUserProfile
    } = useAuth();
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [pendingProvider, setPendingProvider] = useState('');
    const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [emailPending, setEmailPending] = useState(false);
    const isGuest = authIsGuest === true || user?.isGuest === true;
    const shouldShow = open && (!user || isGuest || Boolean(message));

    useEffect(() => {
        if (shouldShow && isGuest) {
            posthog.capture('signup_gate_shown', {
                path: getCurrentAppPath()
            });
        }
    }, [isGuest, shouldShow]);

    if (!shouldShow) {
        return null;
    }

    const handleProvider = async (provider, providerName) => {
        setError('');
        setMessage('');
        setPendingProvider(provider);
        const destination = redirectTo || getCurrentAppPath();

        try {
            if (isGuest && session) {
                const oauthRedirectTo = buildOAuthCallbackUrl(destination, {
                    signup: 'guest_upgrade',
                    provider
                });
                markAuthUpgradeNavigationPending();
                const { error: linkError } = await supabase.auth.linkIdentity({
                    provider,
                    options: {
                        redirectTo: oauthRedirectTo
                    }
                });

                if (linkError) {
                    if (isIdentityAlreadyLinkedError(linkError)) {
                        setError(
                            'This account already has a Nova profile - signing you in.'
                        );
                        const { error: signInError } =
                            await supabase.auth.signInWithOAuth({
                                provider,
                                options: {
                                    redirectTo: oauthRedirectTo
                                }
                            });

                        if (signInError) {
                            throw signInError;
                        }
                        return;
                    }
                    throw linkError;
                }
                return;
            }

            const oauthResult = await loginWithOAuth(provider, {
                redirectTo: buildOAuthCallbackUrl(destination)
            });
            if (oauthResult?.error) {
                throw oauthResult.error;
            }
        } catch (providerError) {
            clearAuthUpgradeNavigationPending();
            console.error(`${providerName} account upgrade failed:`, providerError);
            setError(
                providerError?.message ||
                `${providerName} sign-in failed. Please try again.`
            );
            setPendingProvider('');
        }
    };

    const handleEmailSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setMessage('');

        if (password !== passwordConfirm) {
            setError('Passwords do not match.');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        setEmailPending(true);
        try {
            const { data, error: updateError } = await supabase.auth.updateUser({
                email: email.trim(),
                password
            });
            if (updateError) {
                throw updateError;
            }

            const profile = await ensureUserProfile();
            posthog.capture('signup_completed', {
                method: 'email'
            });

            if (!data?.user?.email_confirmed_at) {
                setMessage('Check your inbox to confirm your email - your squad stays with you');
            } else if (profile?.isGuest === false) {
                setMessage('Your account is ready and your squad is saved.');
            }
        } catch (upgradeError) {
            console.error('Email account upgrade failed:', upgradeError);
            if (isEmailAlreadyRegisteredError(upgradeError)) {
                setError(
                    'This email is already registered. Log in instead, but your guest squad will not carry over when you log in.'
                );
            } else if (isWeakPasswordError(upgradeError)) {
                setError('Choose a stronger password with at least 8 characters.');
            } else {
                setError(upgradeError?.message || 'Email sign-up failed. Please try again.');
            }
        } finally {
            setEmailPending(false);
        }
    };

    const emailHref = isGuest
        ? null
        : buildRegisterRedirectPath(redirectTo || getCurrentAppPath());

    const content = (
        <SignupGateContent
            description={description}
            disabled={Boolean(pendingProvider) || emailPending}
            email={email}
            emailHref={emailHref}
            error={error}
            isEmailFormOpen={isEmailFormOpen}
            isGuest={isGuest}
            message={message}
            onEmailChange={(event) => setEmail(event.target.value)}
            onEmailSubmit={handleEmailSubmit}
            onOpenEmailForm={() => {
                setError('');
                setMessage('');
                setIsEmailFormOpen(true);
            }}
            onPasswordChange={(event) => setPassword(event.target.value)}
            onPasswordConfirmChange={(event) => setPasswordConfirm(event.target.value)}
            onProvider={handleProvider}
            password={password}
            passwordConfirm={passwordConfirm}
            title={title}
        />
    );

    if (inline) {
        return (
            <section className="signup-gate-inline" aria-label={title}>
                {content}
            </section>
        );
    }

    return (
        <Popup
            popOpen
            close={() => onDismiss?.()}
        >
            {content}
        </Popup>
    );
}

export { SignupGateModal };
